import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { drain } from "@utils/import/core/drain";
import { EDocumentImportContext, EDocumentImportDefinition, ReferenceableDocument } from "./types";

/** Codigo de producto o identificacion sin espacios ni mayusculas/minusculas de por medio. */
export const normalizeProductCode = (raw?: unknown): string => `${raw ?? ""}`.trim().toUpperCase();

/**
 * Clave o consecutivo comparables. Las referencias viajan con espacios y a veces con guiones
 * segun el sistema que las escribio, y solo los digitos son significativos.
 */
export const normalizeDocumentNumber = (raw?: unknown): string => `${raw ?? ""}`.replace(/\D/g, "");

/**
 * Identificacion comparable. Las cedulas viajan con guiones y ceros a la izquierda segun
 * quien las escriba, asi que se comparan solo por sus digitos.
 */
export const normalizeIdentification = (raw?: unknown): string =>
    `${raw ?? ""}`.replace(/\D/g, "").replace(/^0+/, "");

const drainProducts = async (): Promise<ProductDataType[]> => {
    const all: ProductDataType[] = [];
    let token: string | undefined = undefined;

    do {
        const { items, nextToken } = await ProductApi.fetchAllForImport(token);
        all.push(...items);
        token = nextToken;
    } while (token);

    return all;
};

export const indexProductsByCode = (
    products: ProductDataType[]
): Map<string, { id: string; codigo: string }> => {
    const index = new Map<string, { id: string; codigo: string }>();

    products.forEach((product) => {
        const code = normalizeProductCode(product.codigo);
        // El primero gana: si el tenant tiene el codigo repetido, se liga al mas antiguo en
        // vez de elegir al azar entre los dos.
        if (code && product.id && !index.has(code)) {
            index.set(code, { id: product.id, codigo: `${product.codigo}` });
        }
    });

    return index;
};

export const indexClientsByIdentification = (
    clients: ClientDataType[]
): Map<string, ClientDataType> => {
    const index = new Map<string, ClientDataType>();

    clients.forEach((client) => {
        const identification = normalizeIdentification(client.identificacion);
        if (identification && !index.has(identification)) index.set(identification, client);
    });

    return index;
};

/**
 * Indexa los documentos referenciables por clave y por consecutivo.
 *
 * Se arman los dos indices porque `InformacionReferencia.Numero` admite cualquiera de los dos
 * ("Clave numerica del comprobante electronico o consecutivo del documento de referencia").
 */
export const indexReferenceableDocuments = (
    documents: ReferenceableDocument[]
): {
    byKey: Map<string, ReferenceableDocument>;
    bySequence: Map<string, ReferenceableDocument>;
} => {
    const byKey = new Map<string, ReferenceableDocument>();
    const bySequence = new Map<string, ReferenceableDocument>();

    documents.forEach((document) => {
        if (!document.id) return;

        const key = normalizeDocumentNumber(document.key);
        const sequence = normalizeDocumentNumber(document.sequence);
        // El primero gana, igual que con los codigos de producto repetidos.
        if (key && !byKey.has(key)) byKey.set(key, document);
        if (sequence && !bySequence.has(sequence)) bySequence.set(sequence, document);
    });

    return { byKey, bySequence };
};

/**
 * Trae de una sola vez todo lo que la carga necesita para resolver los comprobantes.
 *
 * Se precarga en memoria en vez de consultar por documento porque una carga de cientos de
 * comprobantes haria miles de consultas: `Client` ni siquiera tiene indice por identificacion,
 * asi que el indice se arma aqui. Mismo enfoque que `loadExistingClientData` de la importacion
 * de CSV.
 */
export const loadEDocumentImportContext = async (
    definition: EDocumentImportDefinition,
    tenantIdentification: string
): Promise<EDocumentImportContext> => {
    const [products, clients, currencies, existingKeys, referenceable] = await Promise.all([
        drainProducts(),
        drain<ClientDataType>(ClientApi),
        drain<CurrencyDataType>(CurrencyApi),
        definition.loadExistingKeys(),
        definition.requiresReference?.loadDocuments() ?? Promise.resolve([]),
    ]);

    const defaultCurrency = currencies.find((currency) => currency.is_default) || currencies[0] || {};
    const { byKey, bySequence } = indexReferenceableDocuments(referenceable);

    return {
        products,
        productsByCode: indexProductsByCode(products),
        clients,
        clientsByIdentification: indexClientsByIdentification(clients),
        existingKeys,
        referenceableByKey: byKey,
        referenceableBySequence: bySequence,
        tenantIdentification,
        currencies,
        defaultCurrency,
    };
};
